import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Layout({ children }) {
  const { isAdmin, isVisitor, logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/threshold");
  };

  let sessionControls = null;
  if (isAdmin) {
    sessionControls = (
      <>
        <NavLink
          to="/admin/console"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          data-testid="nav-console-link"
        >
          Console
        </NavLink>
        <button
          className="nav-link"
          onClick={handleLogout}
          data-testid="nav-logout-btn"
          title={`${user?.username} · ${user?.alias}`}
        >
          Seal
        </button>
      </>
    );
  } else if (isVisitor) {
    sessionControls = (
      <>
        <span
          className="text-[0.6rem] tracking-[0.35em] uppercase text-[#A3A3A3]"
          data-testid="visitor-badge"
          title={user?.username}
        >
          ◇ {user?.username}
        </span>
        <button
          className="nav-link"
          onClick={handleLogout}
          data-testid="nav-logout-btn"
          title="end visit"
        >
          Depart
        </button>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#E3E1D9] relative">
      <div className="grain-overlay" />
      <div className="scan-lines" />

      {/* Top Banner */}
      <header
        className="sticky top-0 z-50 bg-[#050505] border-b border-[#333333]"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-6">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="nav-home-logo"
          >
            <img
              src="/tiv-logo.png"
              alt="T.I.V Nexus sigil"
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              data-testid="site-logo"
            />
            <div className="leading-tight">
              <div className="font-serif-display text-lg tracking-tighter">
                T·I·V Nexus
              </div>
              <div className="text-[0.55rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
                Terra Infernalis Via — Archive
              </div>
            </div>
          </Link>

          <nav className="flex items-center gap-6 sm:gap-10">
            <NavLink
              to="/"
              end
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-home-link"
            >
              Index
            </NavLink>
            <NavLink
              to="/archive"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-archive-link"
            >
              Archive
            </NavLink>
            <NavLink
              to="/entities"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-entities-link"
            >
              Entities
            </NavLink>
            <NavLink
              to="/realities"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-realities-link"
            >
              Realities
            </NavLink>
            <NavLink
              to="/the-terra-chronicle"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-terra-link"
            >
              Terra Chronicle
            </NavLink>
            <NavLink
              to="/voices"
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid="nav-voices-link"
            >
              Voices
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/chronicle"
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid="nav-chronicle-link"
              >
                VESPER
              </NavLink>
            )}
            {sessionControls}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16 relative z-10">
        {children}
      </main>

      <footer
        className="border-t border-[#333333] mt-24 py-10"
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-[#A3A3A3]">
          <div>
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555555] mb-2">
              Correspondence
            </div>
            <a
              href="mailto:tiv.co.d0c@gmail.com"
              className="block hover:text-[#E3E1D9]"
              data-testid="footer-email"
            >
              tiv.co.d0c@gmail.com
            </a>
            <a
              href="tel:+15033340669"
              className="block hover:text-[#E3E1D9]"
              data-testid="footer-phone"
            >
              (503) 334-0669
            </a>
          </div>
          <div>
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555555] mb-2">
              Disclosure
            </div>
            <p className="leading-relaxed">
              All materials catalogued herein are provided under strict observational protocols.
              Reproduction without consent of the archivist is forbidden.
            </p>
          </div>
          <div className="md:text-right">
            <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555555] mb-2">
              Archivist Access
            </div>
            <Link
              to="/admin"
              className="admin-trigger text-xs"
              data-testid="footer-admin-link"
            >
              [ insert key ]
            </Link>
            <div className="mt-2">
              <Link
                to="/antecessores"
                className="admin-trigger text-[0.6rem] tracking-[0.4em] uppercase"
                data-testid="footer-vault-link"
                title="quattuor antetempus"
              >
                ◌ ante·horam
              </Link>
            </div>
            <div className="mt-3 text-[0.6rem] tracking-[0.35em] uppercase text-[#555555]">
              MMXXVI — T.I.V. Nexus
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
